import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControl, FormHelperText,
  InputLabel, MenuItem, Paper, Select, TextField, Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function BrandHeader() {
  return (
    <>
      <Box sx={{ bgcolor: 'white', px: 3, py: 2.5, textAlign: 'center', borderBottom: '1px solid #e8e8e8' }}>
        <Box component="img" src="/logo.jpg" alt="CONEM" sx={{ height: 52 }} />
      </Box>
      <Box sx={{ background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)', px: 3, py: 2, textAlign: 'center' }}>
        <Typography fontWeight="bold" sx={{ color: 'white', fontSize: '0.92rem', letterSpacing: 0.3 }}>
          CONEM - Consejo Organizacional de Empresas Mexicanas S.A.S. de C.V.
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.82)', display: 'block', mt: 0.3, letterSpacing: 0.2 }}>
          Consultoría Especializada en Protección Civil y Seguridad Industrial
        </Typography>
      </Box>
    </>
  );
}

function BrandFooter() {
  return (
    <Box sx={{ bgcolor: '#f8f9fa', borderTop: '1px solid #e8e8e8', p: 2, textAlign: 'center' }}>
      <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.2 }}>
        ✉ info@conem.com.mx &nbsp;·&nbsp; ☎ 229 129 4389 &nbsp;·&nbsp; www.conem.com.mx
      </Typography>
    </Box>
  );
}

export default function ConfirmarCotizacion() {
  const { folio } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [screen, setScreen] = useState('loading');
  const [blockedMessage, setBlockedMessage] = useState('');
  const [survey, setSurvey] = useState({ razon: '', sugerencia: '' });
  const [surveyError, setSurveyError] = useState('');

  useEffect(() => {
    document.title = 'Confirmar Cotización - CONEM';
  }, []);

  // Antes de ofrecer aceptar/rechazar, confirmar que el enlace sigue vigente (token válido, versión
  // más reciente y aún esperando respuesta); si no, explicar por qué (ej. fue reemplazada).
  const showBlocked = (message) => {
    setBlockedMessage(message || 'Esta cotización ya no está disponible. Contáctanos para más información.');
    setDialogOpen(false);
    setScreen('blocked');
  };

  useEffect(() => {
    fetch(`${API_BASE}/quotations/accept/${folio}/status?token=${encodeURIComponent(token || '')}`)
      .then(res => res.json())
      .then(data => {
        if (data.canDecide) {
          setScreen('dialog');
          setDialogOpen(true);
        } else {
          showBlocked(data.message);
        }
      })
      .catch(() => setScreen('error'));
  }, [folio, token]);

  const handleConfirm = async () => {
    setScreen('loading');
    setDialogOpen(false);
    try {
      const res = await fetch(`${API_BASE}/quotations/accept/${folio}?token=${encodeURIComponent(token || '')}`);
      if (res.ok) {
        setScreen('success');
      } else if (res.status === 403 || res.status === 409) {
        const status = await fetch(`${API_BASE}/quotations/accept/${folio}/status?token=${encodeURIComponent(token || '')}`).then(r => r.json()).catch(() => ({}));
        showBlocked(status.message);
      } else {
        setScreen('error');
      }
    } catch {
      setScreen('error');
    }
  };

  const handleDecline = () => {
    setDialogOpen(false);
    setScreen('survey');
  };

  const handleSurveySubmit = async () => {
    if (!survey.razon) {
      setSurveyError('Por favor selecciona un motivo.');
      return;
    }
    setSurveyError('');
    setScreen('surveyLoading');
    try {
      const res = await fetch(`${API_BASE}/quotations/reject/${folio}?token=${encodeURIComponent(token || '')}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ razon: survey.razon, sugerencia: survey.sugerencia }),
      });
      if (res.status === 403 || res.status === 409) {
        const data = await res.json().catch(() => ({}));
        showBlocked(data.error);
        return;
      }
    } catch {
      // No bloquear al cliente por error de red
    }
    setScreen('surveyDone');
  };

  const cardSx = { maxWidth: 580, width: '100%', borderRadius: 3, overflow: 'hidden' };
  const wideCardSx = { maxWidth: 660, width: '100%', borderRadius: 3, overflow: 'hidden' };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#eef2f7', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>

      {/* Diálogo de confirmación */}
      <Dialog open={dialogOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <Box sx={{ bgcolor: 'white', px: 3, pt: 2.5, pb: 1.5, textAlign: 'center', borderBottom: '1px solid #e8e8e8' }}>
          <Box component="img" src="/logo.jpg" alt="CONEM" sx={{ height: 44 }} />
        </Box>
        <DialogTitle sx={{ background: 'linear-gradient(135deg,#1565c0,#1976d2)', color: 'white', fontWeight: 'bold' }}>
          Confirmar Servicio
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
            Folio: <strong style={{ color: '#1976d2' }}>{folio}</strong>
          </Typography>
          <Typography>
            ¿Deseas confirmar y contratar el servicio cotizado? Al aceptar, nuestro equipo iniciará la coordinación de los trabajos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={handleDecline}
            variant="outlined"
            startIcon={<CancelOutlinedIcon />}
            sx={{ textTransform: 'none', borderColor: '#bdbdbd', color: '#616161' }}
          >
            No por ahora
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="success"
            startIcon={<CheckCircleOutlineIcon />}
            sx={{ textTransform: 'none', fontWeight: 'bold', px: 3 }}
          >
            Sí, confirmar servicio
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carga */}
      {screen === 'loading' && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#1976d2' }} />
          <Typography sx={{ mt: 2, color: '#555' }}>Procesando confirmación...</Typography>
        </Box>
      )}

      {/* Éxito */}
      {screen === 'success' && (
        <Paper elevation={4} sx={wideCardSx}>
          <BrandHeader />
          <Box sx={{ px: 4, pt: 4, pb: 1, textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 68, color: '#43a047', mb: 1.5 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>¡Servicio Confirmado!</Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 0.5 }}>
              Folio: <strong style={{ color: '#1976d2' }}>{folio}</strong>
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
              Nuestro equipo técnico iniciará el proceso de coordinación, integración documental y programación operativa.
            </Typography>
          </Box>

          <Divider sx={{ mx: 4 }} />

          <Box sx={{ px: 4, py: 3 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1565c0', mb: 2, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Próximos pasos
            </Typography>
            {[
              'Confirmación administrativa del servicio contratado.',
              'Asignación de responsable técnico y operativo.',
              'Programación de visita, inspección o capacitación.',
              'Integración documental y desarrollo técnico del proyecto.',
              'Seguimiento y acompañamiento durante el proceso de gestión.',
            ].map((paso, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                <Box sx={{
                  minWidth: 24, height: 24, borderRadius: '50%',
                  bgcolor: '#1976d2', color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.75rem', fontWeight: 'bold', mt: 0.1,
                }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#444' }}>{paso}</Typography>
              </Box>
            ))}

            <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffe082', borderRadius: 2, px: 2.5, py: 1.5, mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#795548', lineHeight: 1.6, display: 'block' }}>
                <strong>Nota:</strong> Los tiempos de resolución dependen de los procesos internos de cada autoridad competente, así como de la información completa proporcionada.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mx: 4 }} />

          <Box sx={{ px: 4, py: 2.5, textAlign: 'center', bgcolor: '#f0f4ff' }}>
            <Typography variant="body2" sx={{ color: '#1565c0', fontStyle: 'italic', fontWeight: 500, lineHeight: 1.6 }}>
              Comprometidos con la seguridad, cumplimiento y continuidad operativa de su empresa.
            </Typography>
          </Box>

          <BrandFooter />
        </Paper>
      )}

      {/* Enlace ya no vigente (reemplazada, cancelada, ya respondida o enlace inválido) */}
      {screen === 'blocked' && (
        <Paper elevation={4} sx={cardSx}>
          <BrandHeader />
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <FeedbackOutlinedIcon sx={{ fontSize: 72, color: '#1976d2', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>Cotización no disponible</Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
              {blockedMessage}
            </Typography>
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="body2" color="text.secondary">
              ✉ <strong>info@conem.com.mx</strong> &nbsp;·&nbsp; ☎ <strong>229 129 4389</strong>
            </Typography>
          </Box>
          <BrandFooter />
        </Paper>
      )}

      {/* Error */}
      {screen === 'error' && (
        <Paper elevation={4} sx={cardSx}>
          <BrandHeader />
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CancelOutlinedIcon sx={{ fontSize: 72, color: '#e53935', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>Ocurrió un error</Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
              No pudimos procesar tu confirmación en este momento. Por favor contáctanos directamente y con gusto te atendemos.
            </Typography>
            <Divider sx={{ my: 2.5 }} />
            <Typography variant="body2" color="text.secondary">
              ✉ <strong>info@conem.com.mx</strong> &nbsp;·&nbsp; ☎ <strong>229 129 4389</strong>
            </Typography>
          </Box>
          <BrandFooter />
        </Paper>
      )}

      {/* Encuesta */}
      {screen === 'survey' && (
        <Paper elevation={4} sx={cardSx}>
          <BrandHeader />

          <Box sx={{ px: 4, pt: 3.5, pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
              <FeedbackOutlinedIcon sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                Ayúdanos a Mejorar
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              Lamentamos que no hayas podido confirmar en este momento. Tu opinión es muy valiosa para nosotros
              y nos ayuda a ofrecerte una mejor propuesta en el futuro.
            </Typography>
            <Divider sx={{ mb: 3 }} />
          </Box>

          <Box sx={{ px: 4, pb: 4 }}>
            <FormControl fullWidth error={!!surveyError} sx={{ mb: 3 }}>
              <InputLabel id="razon-label">¿Por qué no confirmas el servicio? *</InputLabel>
              <Select
                labelId="razon-label"
                value={survey.razon}
                label="¿Por qué no confirmas el servicio? *"
                onChange={e => { setSurvey(p => ({ ...p, razon: e.target.value })); setSurveyError(''); }}
              >
                <MenuItem value="precio">El precio está fuera de mi presupuesto</MenuItem>
                <MenuItem value="tiempo">No es el momento adecuado</MenuItem>
                <MenuItem value="competencia">Opté por otro proveedor</MenuItem>
                <MenuItem value="info">Necesito más información antes de decidir</MenuItem>
                <MenuItem value="otro">Otro motivo</MenuItem>
              </Select>
              {surveyError && <FormHelperText>{surveyError}</FormHelperText>}
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="¿Qué podríamos mejorar? (opcional)"
              placeholder="Comparte cualquier comentario que nos ayude a brindarte un mejor servicio..."
              value={survey.sugerencia}
              onChange={e => setSurvey(p => ({ ...p, sugerencia: e.target.value }))}
              variant="outlined"
              sx={{ mb: 3.5 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                onClick={() => { setDialogOpen(true); setScreen('dialog'); }}
                variant="text"
                sx={{ textTransform: 'none', color: '#757575' }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSurveySubmit}
                variant="contained"
                size="large"
                startIcon={<SentimentDissatisfiedIcon />}
                sx={{ textTransform: 'none', fontWeight: 'bold', px: 4, borderRadius: 2, bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
              >
                Enviar comentarios
              </Button>
            </Box>
          </Box>

          <BrandFooter />
        </Paper>
      )}

      {/* Enviando encuesta */}
      {screen === 'surveyLoading' && (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#1976d2' }} />
          <Typography sx={{ mt: 2, color: '#555' }}>Enviando tus comentarios...</Typography>
        </Box>
      )}

      {/* Encuesta enviada */}
      {screen === 'surveyDone' && (
        <Paper elevation={4} sx={cardSx}>
          <BrandHeader />
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: '#1976d2', mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>¡Gracias por tu opinión!</Typography>
            <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
              Hemos recibido tus comentarios. Si en algún momento deseas retomar la cotización o necesitas más información, no dudes en contactarnos.
            </Typography>
            <Divider sx={{ mb: 2.5 }} />
            <Typography variant="body2" color="text.secondary">
              ✉ <strong>info@conem.com.mx</strong> &nbsp;·&nbsp; ☎ <strong>229 129 4389</strong>
            </Typography>
          </Box>
          <BrandFooter />
        </Paper>
      )}

    </Box>
  );
}
